import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminNavigationDrawer } from '../../components/admin/AdminNavigationDrawer';
import { useOTPAuth } from '../../hooks/useOTPAuth';
import { adminService, ChatSession } from '../../services/adminService';

export const MonitorChatsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, createAnonymousSession } = useOTPAuth();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isDrawerPinned, setIsDrawerPinned] = useState(false);

  const handleLogout = async () => {
    await logout();
    // Create a new anonymous session after logout so user can continue chatting
    await createAnonymousSession();
    // Redirect to chat page
    window.location.href = '/chat';
  };

  // Fetch sessions on mount
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await adminService.getAllSessions();
        setSessions(data);
      } catch (err: any) {
        console.error('Error fetching sessions:', err);
        setError(err.response?.data?.error || err.message || 'Failed to load sessions');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessions();
  }, []);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="flex h-screen bg-neutral-white dark:bg-neutral-black">
      {/* Admin Navigation Drawer */}
      <AdminNavigationDrawer
        user={user}
        onLogout={handleLogout}
        isMobileOpen={isMobileDrawerOpen}
        onMobileClose={() => setIsMobileDrawerOpen(false)}
        onPinChange={setIsDrawerPinned}
      />

      {/* Main Content */}
      <div className={`flex-1 flex flex-col ${isDrawerPinned ? 'md:ml-64' : 'md:ml-16'}`}>
        {/* Header */}
        <header className="bg-neutral-white dark:bg-neutral-black border-b border-neutral-light-gray dark:border-neutral-dark-gray px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Mobile hamburger menu */}
              <button
                onClick={() => setIsMobileDrawerOpen(true)}
                className="md:hidden p-2 hover:bg-gray-100 rounded transition-colors"
                title="Open menu"
              >
                <svg className="w-6 h-6 text-[#080808]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-2xl font-semibold text-neutral-black dark:text-neutral-white tracking-tight">
                Monitor Chats
              </h1>
            </div>
            <div className="text-sm text-gray-500">
              {sessions.length} total sessions
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Error Message */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <img src="/images/logos/loader-circle.svg" alt="Loading..." className="h-8 w-8" />
                <p className="ml-3 text-gray-600">Loading sessions...</p>
              </div>
            )}

            {/* Sessions - Table for larger screens */}
            {!isLoading && !error && (
              <>
                {/* Desktop Table View */}
                <div className="hidden lg:block bg-white dark:bg-neutral-dark-gray rounded-lg border border-neutral-light-gray dark:border-neutral-dark-gray overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 dark:bg-neutral-black">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Activity
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Messages
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cost
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-neutral-dark-gray divide-y divide-gray-200">
                        {sessions.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                              No chat sessions found
                            </td>
                          </tr>
                        ) : (
                          sessions.map((session) => (
                            <tr
                              key={session.sessionId}
                              onClick={() => navigate(`/admin/sessions/${session.sessionId}`)}
                              className="hover:bg-gray-50 dark:hover:bg-neutral-black transition-colors cursor-pointer"
                            >
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-neutral-white">
                                {formatDateTime(session.lastActivity)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  {session.isAnonymous && (
                                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                  )}
                                  <span className={`text-sm ${session.isAnonymous ? 'text-gray-500 italic' : 'text-gray-900 dark:text-neutral-white'}`}>
                                    {session.userEmail}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-neutral-white">
                                {session.messageCount}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-neutral-white">
                                ${session.estimatedCostUsd.toFixed(4)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {session.hasError ? (
                                  <div className="flex items-center">
                                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    <span className="ml-2 text-sm text-red-600">Error</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center">
                                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="ml-2 text-sm text-green-600">OK</span>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden space-y-4">
                  {sessions.length === 0 ? (
                    <div className="bg-white dark:bg-neutral-dark-gray rounded-lg border border-neutral-light-gray dark:border-neutral-dark-gray p-8 text-center">
                      <p className="text-sm text-gray-500">No chat sessions found</p>
                    </div>
                  ) : (
                    sessions.map((session) => (
                      <div
                        key={session.sessionId}
                        onClick={() => navigate(`/admin/sessions/${session.sessionId}`)}
                        className="bg-white dark:bg-neutral-dark-gray rounded-lg border border-neutral-light-gray dark:border-neutral-dark-gray p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      >
                        {/* User and Status Row */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center flex-1 min-w-0">
                            {session.isAnonymous && (
                              <svg className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            )}
                            <span className={`text-sm font-medium truncate ${session.isAnonymous ? 'text-gray-500 italic' : 'text-gray-900 dark:text-neutral-white'}`}>
                              {session.userEmail}
                            </span>
                          </div>
                          {session.hasError ? (
                            <div className="flex items-center ml-2 flex-shrink-0">
                              <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            </div>
                          ) : (
                            <div className="flex items-center ml-2 flex-shrink-0">
                              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>

                        {/* Session Details */}
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Messages</p>
                            <p className="font-medium text-gray-900 dark:text-neutral-white">{session.messageCount}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Cost</p>
                            <p className="font-medium text-gray-900 dark:text-neutral-white">${session.estimatedCostUsd.toFixed(4)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Status</p>
                            <p className={`font-medium ${session.hasError ? 'text-red-600' : 'text-green-600'}`}>
                              {session.hasError ? 'Error' : 'OK'}
                            </p>
                          </div>
                        </div>

                        {/* Last Activity */}
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-neutral-dark-gray">
                          <p className="text-xs text-gray-500">
                            Last activity: <span className="text-gray-900 dark:text-neutral-white">{formatDateTime(session.lastActivity)}</span>
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};
